import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import ROUTES from '@/common/routes';
import { useAuth, useAuthorization } from '@/modules/user/hooks';
import { useAuthStore } from '@/modules/user/stores';
import { GLOBAL_ROUTES } from '@/pages';
import Token from '@/utils/handlers/Token';
import { matchPath } from '@/utils/modifiers';

const MINIFIED_QUERY_KEY = 'minified';
const ERROR_QUERY_KEY = 'error_description';

const LOGOUT_ROUTES = [ROUTES.USER_ACTIVATION];
const EXCLUDED_SAVED_PATH_ROUTES = new Set([ROUTES.RESET_PASSWORD, ROUTES.CHANGE_PASSWORD]);

const useAppRedirect = () => {
  const [savedPath, setSavedPath] = useState<string>();

  useAuth();
  const { handleLogout } = useAuthorization();
  const { auth, inited } = useAuthStore();

  const history = useHistory();

  const location = useLocation();

  const query = useMemo(() => new URLSearchParams(location?.search || ''), [location]);

  const pathname = useMemo(() => location.pathname, [location]);

  const openedRoutes = useMemo(() => GLOBAL_ROUTES.filter((item) => !item.authed), []);
  const routes = useMemo(() => {
    if (!auth && inited) {
      return openedRoutes;
    }
    return GLOBAL_ROUTES;
  }, [auth, inited, openedRoutes]);

  // const error = location.hash.split('&').find((part) => (part.startsWith(ERROR_QUERY_KEY) || part.startsWith('access_token'))) || '';
  const errorQuery = useMemo(() => location.hash.includes(ERROR_QUERY_KEY), [location.hash]);
  const errorMode = useMemo(() => pathname === ROUTES.ERROR, [pathname]);

  useEffect(() => {
    if (!auth && pathname !== ROUTES.LOGIN && pathname !== ROUTES.SIGN_UP
      && !EXCLUDED_SAVED_PATH_ROUTES.has(pathname)) {
      setSavedPath(`${pathname}${location?.search || ''}`);
    }
    if (Token.isAuthenticated()) {
      if (LOGOUT_ROUTES.includes(pathname)) {
        handleLogout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const redirectByAuthCondition = useCallback(() => {
    if (errorQuery && pathname !== ROUTES.ERROR) {
      history.push(`${ROUTES.ERROR}`);
      return;
    }
    if (Token.getSSODataFromHash()) {
      return;
    }
    if (!auth && !routes.find((route) => matchPath(`${route.path}`, pathname))) {
      history.push(ROUTES.LOGIN);
    }
    if (auth && pathname === ROUTES.LOGIN) {
      if (savedPath) {
        history.push(savedPath);
        setSavedPath(undefined);
      } else {
        history.push('/');
      }
    }
    if (auth && (pathname === ROUTES.CHANGE_PASSWORD
      || pathname === ROUTES.RESET_PASSWORD
      || pathname === ROUTES.USER_ACTIVATION)) {
      history.push('/');
    }
  }, [auth, errorQuery, history, pathname, routes, savedPath]);
  useEffect(() => {
    if (inited) redirectByAuthCondition();
  }, [redirectByAuthCondition, inited]);

  return {
    loading: !inited,
    auth,
    routes,
    minifiedQuery: query.get(MINIFIED_QUERY_KEY),
    errorMode,
  };
};

export default useAppRedirect;
